import clsxm from "@/lib/clsxm";
import {
  memo,
  useEffect,
  useId,
  useMemo,
  useRef,
  type FC,
  type HTMLInputTypeAttribute,
  type ReactNode,
  type RefObject,
} from "react";
import {useTranslation} from "react-i18next";

const InputField: FC<{
  ref?: RefObject<HTMLInputElement | null>;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  error?: string | null;
  icon?: ReactNode;
  type?: HTMLInputTypeAttribute;
  label?: string | ReactNode;
  value?: string | null;
  placeholder?: string;
  className?: string;
  onClick?: () => void;
  isDisabled?: boolean;
  isReadonly?: boolean;
  labelClassName?: string;
  containerClassName?: string;
  name?: string;
  customFieldId?: string;
  maxLength?: number;
  optional?: boolean;
}> = ({
  ref,
  onChange,
  onBlur,
  error = null,
  icon = null,
  label,
  placeholder,
  type = "text",
  value,
  className,
  onClick,
  isDisabled,
  isReadonly = false,
  labelClassName,
  containerClassName,
  name,
  customFieldId,
  maxLength,
  optional = false,
}) => {
  const {t} = useTranslation("common");
  const fieldId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const isInputFieldDisabled = useMemo(() => isDisabled || !onChange, [isDisabled, onChange]);

  useEffect(() => {
    if (inputRef.current && error) {
      inputRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [error]);

  return (
    <div className={clsxm("flex w-full flex-col gap-1", containerClassName)}>
      {!!label && (
        <label
          htmlFor={customFieldId || fieldId}
          className={clsxm("text-neutral-grey-dark flex min-h-5 flex-row gap-2 text-sm font-medium", labelClassName)}>
          {label}
          {optional && (
            <div className="bg-neutral-grey-lightest text-neutral-grey-dark flex items-center justify-center rounded-full px-2 text-[8px] font-normal first-letter:uppercase">
              {t("optional")}
            </div>
          )}
        </label>
      )}
      <div className="relative" onClick={() => !isInputFieldDisabled && onClick && onClick()} ref={ref}>
        <input
          readOnly={isReadonly}
          disabled={isInputFieldDisabled}
          id={customFieldId || fieldId}
          className={clsxm(
            "border-neutral-grey-light text-neutral-black placeholder:text-neutral-grey-light focus:border-primary-medium-solid read-only:enabled:hover:bg-neutral-white-dark disabled:bg-neutral-white-dark disabled:text-neutral-grey-dark flex h-14 w-full rounded-md border bg-white p-4 outline-hidden transition-all duration-150 read-only:enabled:cursor-pointer disabled:cursor-not-allowed",
            error && "border-danger-medium-red focus:border-danger-dark-red",
            className,
          )}
          type={type}
          placeholder={placeholder}
          maxLength={maxLength}
          {...(typeof value !== "undefined" && value !== null && {value})}
          ref={inputRef}
          name={name}
          onChange={({target: {value}}) => onChange?.(value)}
          onBlur={({target: {value}}) => onBlur?.(value)}
        />
        {icon}
      </div>
      {!!error && <span className="text-danger-medium-red mt-0.5 text-xs">{t(error)}</span>}
    </div>
  );
};

export default memo(InputField);
